/* MQS V198: visible thumbnail-strip arrows and truthful visible-media counter.
 * Uses Warehouse's own carousel through a narrowly scoped custom event.
 * No replacement zoom, no image edits, no handlers on unrelated carousels.
 */
(() => {
  'use strict';
  const instances = new Map();
  class GalleryNavigation {
    constructor(gallery) {
      this.gallery = gallery;
      this.carousel = gallery.querySelector('.product-gallery__carousel');
      this.list = gallery.querySelector('.product-gallery__thumbnail-list');
      this.counter = gallery.querySelector('[data-mqs-gallery-counter]');
      this.hint = gallery.querySelector('[data-mqs-gallery-hint]');
      this.arrows = [...gallery.querySelectorAll('[data-mqs-gallery-step]')];
      this.events = new AbortController();
      if (!this.carousel || !this.list || !this.arrows.length) return;
      this.arrows.forEach(button => button.addEventListener('click', () => this.move(Number(button.dataset.mqsGalleryStep)), {signal:this.events.signal}));
      this.observer = new MutationObserver(() => this.schedule());
      this.observer.observe(gallery, {subtree:true, childList:true, attributes:true, attributeFilter:['class','data-mqs-navigation-ready']});
      this.resize = new ResizeObserver(() => this.schedule());
      this.resize.observe(this.carousel);
      this.schedule();
    }
    schedule() {
      if (this.scheduled) return;
      this.scheduled = requestAnimationFrame(() => { this.scheduled = 0; this.update(); });
    }
    visible() {
      return [...this.list.querySelectorAll('.product-gallery__thumbnail:not(.is-filtered)')];
    }
    state() {
      const thumbs = this.visible();
      const selected = this.carousel.querySelector('.product-gallery__carousel-item.is-selected:not(.is-filtered)');
      let index = thumbs.findIndex(thumb => selected && thumb.dataset.mediaId === selected.dataset.mediaId);
      if (index < 0) index = thumbs.findIndex(thumb => thumb.classList.contains('is-nav-selected'));
      return {thumbs, index:Math.max(0,index)};
    }
    update() {
      const {thumbs,index} = this.state();
      const ready = this.carousel.dataset.mqsNavigationReady === 'true';
      const count = thumbs.length;
      const hasOtherMedia = [...this.carousel.querySelectorAll('.product-gallery__carousel-item:not(.is-filtered)')].some(cell => cell.dataset.mediaType !== 'image');
      const mediaWord = hasOtherMedia ? 'Media' : 'Photo';
      if (this.counter) {
        const text = count ? mediaWord + ' ' + (index + 1) + ' of ' + count : 'No photos for this selection';
        if (this.counter.textContent !== text) this.counter.textContent = text;
      }
      if (this.hint) {
        const text = count > 1 ? (hasOtherMedia ? 'Use the arrows to see all images and videos' : 'More photos — use the arrows') : 'All photos shown';
        if (this.hint.textContent !== text) this.hint.textContent = text;
      }
      this.arrows.forEach(button => {
        const step = Number(button.dataset.mqsGalleryStep);
        button.disabled = !ready || count < 2 || (step < 0 ? index === 0 : index >= count - 1);
        button.setAttribute('aria-label', (step < 0 ? 'Previous' : 'Next') + (hasOtherMedia ? ' product image or video' : ' product photo') + (step > 0 ? ' — see more' : ''));
      });
      thumbs.forEach((thumb,i) => {
        thumb.setAttribute('aria-current', i === index ? 'true' : 'false');
        if (!thumb.hasAttribute('aria-label')) thumb.setAttribute('aria-label', 'View product photo or media ' + (i + 1));
      });
      const height = Math.max(160, Math.min(450, this.carousel.clientHeight - 90));
      const value = height + 'px';
      if (this.gallery.style.getPropertyValue('--mqs-thumbnail-height') !== value) this.gallery.style.setProperty('--mqs-thumbnail-height',value);
    }
    move(step) {
      if (this.carousel.dataset.mqsNavigationReady !== 'true') return;
      const {thumbs,index} = this.state();
      const next = thumbs[index + step];
      if (!next) return;
      this.gallery.dispatchEvent(new CustomEvent('mqs:gallery-navigate', {bubbles:true, detail:{mediaId:next.dataset.mediaId}}));
      this.schedule();
    }
    destroy() {
      this.events.abort(); this.observer?.disconnect(); this.resize?.disconnect();
      if (this.scheduled) cancelAnimationFrame(this.scheduled);
    }
  }
  function init(root=document) {
    if (root.matches?.('[data-mqs-gallery]')) attach(root);
    root.querySelectorAll?.('[data-mqs-gallery]').forEach(attach);
  }
  function attach(gallery) {
    if (!instances.has(gallery)) instances.set(gallery, new GalleryNavigation(gallery));
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => init(), {once:true}); else init();
  document.addEventListener('shopify:section:load',event => init(event.target));
  document.addEventListener('shopify:section:unload',event => {
    instances.forEach((instance,gallery) => {
      if (event.target.contains(gallery)) { instance.destroy(); instances.delete(gallery); }
    });
  });
  // Product quick view is inserted dynamically by Warehouse.
  new MutationObserver(records => {
    records.forEach(record => record.addedNodes.forEach(node => { if (node.nodeType === 1) init(node); }));
    instances.forEach((instance,gallery) => { if (!gallery.isConnected) { instance.destroy(); instances.delete(gallery); } });
  }).observe(document.documentElement, {childList:true,subtree:true});
})();
